/**
 * 버그리포트 쿼리 키 팩토리.
 *
 * 이 도메인은 키가 전부 인라인 리터럴이었다(조회 8 · 무효화 11). `queryKey` 는
 * `readonly unknown[]` 이라 오타를 타입이 못 잡으므로, 루트를 잘못 적으면 prefix 매칭이
 * 한 건도 안 걸려 **조용히** 아무것도 무효화되지 않는다. 실제로 그 계열 결함이
 * 워크로그(`['workLog']`)와 근태(`['attendance']`)에서 났었다(문서 §2).
 *
 * ⚠️ **`'bug-reports'` 는 이 레포에서 두 가지 뜻으로 쓰인다** — 이 쿼리 키 루트이면서
 * 동시에 **윈도우/뷰 ID**(`windowRegistry` · `appNav` · `getDeepLink` · `appViewIcon` ·
 * `carouselDockTones`)다. 문자열만 보고 일괄 치환하면 앱 네비게이션이 깨진다.
 *
 * 회귀 가드: `__tests__/bugReportKeys.test.ts`.
 */
import { defineQueryKeys } from '@/shared/lib/queryKeys';

export const bugReportKeys = defineQueryKeys('bug-reports', (all) => ({
  /** 목록 — `useInfiniteQuery` 라 캐시 모양이 `{ pages, pageParams }` 다(아래 `hover` 주석 참고). */
  list: () => [...all, 'list'] as const,

  /**
   * 상세.
   *
   * **세그먼트를 넣었다.** 예전에는 `['bug-reports', id]` 로 uuid 가 리터럴(`'list'` ·
   * `'hotspots'` · `'stats'` · `'suggestions'`)과 **같은 칸**을 나눠 썼다. id 가 우연히 그
   * 리터럴이면 다른 조회에 걸리고, "상세 전부" 접두사도 만들 수 없다(문서 §10-2).
   */
  detail: (id: string) => [...all, 'detail', id] as const,

  /**
   * 상세에 딸린 것 둘 — `detail` **하위**에 둔다.
   *
   * 예전에도 `['bug-reports', id]` 가 이 둘의 접두사여서 상세 무효화가 함께 덮었다.
   * 세그먼트를 옮기면서 그 관계를 그대로 유지한다(동작 보존).
   */
  events: (id: string) => [...all, 'detail', id, 'events'] as const,
  votes: (id: string) => [...all, 'detail', id, 'votes'] as const,

  /** 다발 지점 — 목록과 별개 엔드포인트. */
  hotspots: () => [...all, 'hotspots'] as const,

  /** 통계 둘. `months` 가 바뀌면 별개 엔트리다(`keepPreviousData` 로 깜빡임만 막는다). */
  monthlyStats: (months: number) => [...all, 'stats', 'monthly', months] as const,
  processorStats: (months: number) => [...all, 'stats', 'processor', months] as const,

  /** 화면 맥락별 제안 — `pageContext` 가 키에 실린다. */
  suggestions: (pageContext: string) => [...all, 'suggestions', pageContext] as const,
}));
